import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { Contest } from '@/api/contests/types';
import { IconUsers } from '@/blocks/components/Icons';
import { IconCompetition } from '@/blocks/components/Icons/IconCompetition';
import { LinkSignup } from '@/blocks/components/PlatformLink';
import { FormatDate } from '@/blocks/formatters/format-date';
import { FormatNumber } from '@/blocks/formatters/format-number';
import { FormatAmount } from '@/blocks/formatters/format-price';
import buttons from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';
import { isFiniteNumber } from '@/utils/number';

import styles from './TradingContestsOngoingCard.module.scss';
import { countdown } from './countdown';

type CountdownTimerUnitProps = {
  value: string;
  label: ReactNode;
};

const CountdownTimerUnit = ({ value, label }: CountdownTimerUnitProps) => {
  return (
    <div className={styles.countdownTime}>
      <span className={classNames(typography.h4, styles.countdownTimeValue)}>{value}</span>
      <span className={classNames(typography.bodyXS, styles.countdownTimeLabel)}>{label}</span>
    </div>
  );
};

export const TradingContestsOngoingCard = ({
  id,
  name,
  totalAwards,
  participants,
  startAt,
  closeAt,
  currency,
  participantsLimit,
}: Contest) => {
  const tr = useTranslations();

  const getCountdown = useCallback(
    () =>
      countdown(
        null,
        closeAt,
        countdown.DAYS | countdown.HOURS | countdown.MINUTES | countdown.SECONDS,
      ) as unknown as Record<string, number>,
    [closeAt],
  );

  const [timeLeft, setTimeLeft] = useState(getCountdown());

  useEffect(() => {
    const interval = setInterval(() => setTimeLeft(getCountdown()), 1000);
    return () => clearInterval(interval);
  }, [closeAt, getCountdown]);

  return (
    <div className={styles.card}>
      <span className={classNames(typography.h6, styles.title)}>{name}</span>
      <div className={styles.countdown}>
        <span className={classNames(typography.bodyS, styles.countdownEndsIn)}>
          {tr('pages.promotions.tradingContests.sections.ongoing.endsIn')}
        </span>
        <div className={styles.countdownContainer}>
          <CountdownTimerUnit
            value={timeLeft.days.toString().padStart(2, '0')}
            label={tr.rich('pages.promotions.tradingContests.sections.ongoing.days')}
          />
          <span className={styles.separator}>:</span>
          <CountdownTimerUnit
            value={timeLeft.hours.toString().padStart(2, '0')}
            label={tr.rich('pages.promotions.tradingContests.sections.ongoing.hours')}
          />
          <span className={styles.separator}>:</span>
          <CountdownTimerUnit
            value={timeLeft.minutes.toString().padStart(2, '0')}
            label={tr.rich('pages.promotions.tradingContests.sections.ongoing.minutes')}
          />
          <span className={styles.separator}>:</span>
          <CountdownTimerUnit
            value={timeLeft.seconds.toString().padStart(2, '0')}
            label={tr.rich('pages.promotions.tradingContests.sections.ongoing.seconds')}
          />
        </div>
      </div>
      <div className={styles.contestStats}>
        <div className={styles.contestStat}>
          <IconCompetition />
          <span className={classNames(typography.bodyS, styles.contestStatValue)}>
            <FormatAmount value={totalAwards} currency={currency} />
          </span>
        </div>
        <div className={styles.contestStat}>
          <IconUsers />
          <span className={classNames(typography.bodyS, styles.contestStatValue)}>
            <FormatNumber value={participants} />
            {isFiniteNumber(participantsLimit) ? (
              <>
                {' '}
                /{' '}
                <span className={styles.transparentText}>
                  <FormatNumber value={participantsLimit} />
                </span>
              </>
            ) : null}
          </span>
        </div>
        <div className={styles.contestStat}>
          <span className={classNames(typography.bodyXS, styles.contestStatLabel)}>
            {tr('pages.promotions.tradingContests.sections.ongoing.start')}
          </span>
          <span className={classNames(typography.bodyS, styles.contestStatValue)}>
            <FormatDate date={startAt} options={{ month: '2-digit', day: '2-digit', year: 'numeric' }} />
          </span>
          <span className={classNames(typography.bodyXS, styles.contestStatLabel)}>
            <FormatDate date={startAt} options={{ hour: '2-digit', minute: '2-digit', second: 'numeric' }} />
          </span>
        </div>
        <div className={styles.contestStat}>
          <span className={classNames(typography.bodyXS, styles.contestStatLabel)}>
            {tr('pages.promotions.tradingContests.sections.ongoing.end')}
          </span>
          <span className={classNames(typography.bodyS, styles.contestStatValue)}>
            <FormatDate date={closeAt} options={{ month: '2-digit', day: '2-digit', year: 'numeric' }} />
          </span>
          <span className={classNames(typography.bodyXS, styles.contestStatLabel)}>
            <FormatDate date={closeAt} options={{ hour: '2-digit', minute: '2-digit', second: 'numeric' }} />
          </span>
        </div>
      </div>
      <div className={styles.ctas}>
        <LinkSignup
          params={{
            redirect: `/my/contests/all/${id}`,
          }}
          className={classNames(buttons.btn, buttons.btnResponsiveSM, buttons.btnPrimary)}
          data-event-label={tr.rich('en.pages.promotions.tradingContests.sections.ongoing.join')}
        >
          {tr('pages.promotions.tradingContests.sections.ongoing.join')}
        </LinkSignup>
        <LinkSignup
          params={{
            redirect: `/my/contests/all/${id}`,
          }}
          className={classNames(buttons.btn, buttons.btnResponsiveSM, buttons.btnOutline)}
          data-event-label={tr.rich('en.pages.promotions.tradingContests.sections.ongoing.view')}
        >
          {tr('pages.promotions.tradingContests.sections.ongoing.view')}
        </LinkSignup>
      </div>
    </div>
  );
};
